// client/src/admin/dashboard/TopBar.js
import React, { useState } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  useTheme,
  Tooltip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  LockReset as LockResetIcon,
} from "@mui/icons-material";
import ThemeToggle from "../../common/ThemeToggle";
import AdminChangePassword from "../change-password/AdminChangePassword";
import { TranslateContext } from "../../context/TranslateContext";

const TopBar = ({
  lang,
  isMobile,
  isDark,
  selectedMenu,
  toggleColorMode,
  handleDrawerToggle,
}) => {
  const theme = useTheme();
  const { lang: ctxLang } = React.useContext(TranslateContext);
  const t = (k) => lang?.[k] || ctxLang?.[k] || k;
  const [changePwdOpen, setChangePwdOpen] = useState(false);

  const label = selectedMenu
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

  return (
    <>
      {/* ── Mobile AppBar ── */}
      {isMobile && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            bgcolor: "background.paper",
            borderBottom: `1px solid ${theme.palette.divider}`,
            backdropFilter: "blur(12px)",
            color: "text.primary",
          }}
        >
          <Toolbar sx={{ minHeight: "52px !important" }}>
            <IconButton
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, color: "text.primary" }}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              variant="h6"
              sx={{ flexGrow: 1, fontWeight: 700, fontSize: "0.95rem" }}
            >
              Admin
            </Typography>
            <Tooltip title={t("changePassword") || "Change Password"}>
              <IconButton
                size="small"
                onClick={() => setChangePwdOpen(true)}
                sx={{
                  color: "text.secondary",
                  mr: 0.5,
                  "&:hover": { color: "primary.main" },
                }}
              >
                <LockResetIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <ThemeToggle size="small" />
          </Toolbar>
        </AppBar>
      )}

      {/* ── Desktop TopBar ── */}
      {!isMobile && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 1.5,
            bgcolor: "background.paper",
            borderBottom: `1px solid ${theme.palette.divider}`,
            backdropFilter: "blur(12px)",
            position: "sticky",
            top: 0,
            zIndex: theme.zIndex.appBar,
          }}
        >
          {/* Breadcrumb */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
            <Typography variant="caption" color="text.disabled">
              Admin
            </Typography>
            <Typography variant="caption" color="text.disabled">
              /
            </Typography>
            <Typography variant="caption" fontWeight={600} color="text.primary">
              {label}
            </Typography>
          </Box>

          {/* Right controls */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title={t("changePassword") || "Change Password"} arrow>
              <IconButton
                size="small"
                onClick={() => setChangePwdOpen(true)}
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "8px",
                  bgcolor: "action.hover",
                  border: `1px solid ${theme.palette.divider}`,
                  color: "text.secondary",
                  "&:hover": {
                    bgcolor: "action.selected",
                    color: "primary.main",
                  },
                }}
              >
                <LockResetIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>

            <ThemeToggle size="small" />

            <Avatar
              sx={{
                width: 30,
                height: 30,
                fontSize: 12,
                fontWeight: 700,
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                border: `2px solid ${theme.palette.divider}`,
              }}
            >
              A
            </Avatar>
          </Box>
        </Box>
      )}

      {/* ── Change Password Dialog ── */}
      <AdminChangePassword
        open={changePwdOpen}
        onClose={() => setChangePwdOpen(false)}
        lang={lang}
      />
    </>
  );
};

export default TopBar;
